/**
 * CompanySettingsContext — carica /api/settings/company al mount e lo espone
 * a tutta l'app. Applica automaticamente `colore_primario`/`colore_accento`
 * come CSS variables sul <html> così che il branding si propaghi ovunque.
 *
 * Reagisce al broadcast WebSocket `settings.company.updated` per aggiornare
 * il branding senza refresh.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/api";

const CompanyCtx = createContext(null);

const DEFAULTS = {
  ragione_sociale: "Ethos Costruzioni",
  nome_breve: "ETHOS",
  colore_primario: "#0f172a",
  colore_accento: "#f97316",
  logo_base64: "",
};

function applyBranding(cfg) {
  const root = document.documentElement;
  if (cfg.colore_primario) root.style.setProperty("--brand-primary", cfg.colore_primario);
  if (cfg.colore_accento) root.style.setProperty("--brand-accent", cfg.colore_accento);
  // Aggiorna title
  if (cfg.ragione_sociale) document.title = cfg.ragione_sociale;
}

export function CompanySettingsProvider({ children }) {
  const [company, setCompany] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      // Timeout basso: se l'endpoint è lento/rotto in produzione
      // non deve bloccare l'accesso all'app — si usano i default.
      const r = await api.get("/settings/company", { timeout: 4000 });
      setCompany({ ...DEFAULTS, ...(r.data || {}) });
      applyBranding(r.data || {});
    } catch {
      // fallback silenzioso ai defaults
      applyBranding(DEFAULTS);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ company, loaded, refresh }), [company, loaded, refresh]);
  return <CompanyCtx.Provider value={value}>{children}</CompanyCtx.Provider>;
}

export function useCompanySettings() {
  const ctx = useContext(CompanyCtx);
  if (!ctx) throw new Error("useCompanySettings deve stare dentro <CompanySettingsProvider>");
  return ctx;
}
